/**
 * Client data configuration file
 * Contains all client information including logos, services, and social media links
 *
 * Dosya amacı:
 * - Uygulamanın portfolio sayfasında ve client detay sayfalarında kullanılacak müşteri verilerini içerir.
 * - Orijinal medya (videolar / görseller) listeleri burada tutulur.
 * - Bu dosyada belirtilen YouTube kısa linkleri embed formatına dönüştürülerek mediaLinks.videos dizilerine eklendi.
 */

/**
 * ClientSocialLinks interface
 *
 * - Müşteri sosyal medya linklerini tanımlar
 */
export interface ClientSocialLinks {
  facebook?: string;
  instagram?: string;
  youtube?: string;
  linkedin?: string;
}

/**
 * ClientMediaLinks interface
 *
 * - Müşteriye ait medya linklerinin yapısını tanımlar
 */
export interface ClientMediaLinks {
  videos: string[];
  images: string[];
}

/**
 * Client interface
 *
 * - Tek bir müşteri kaydının yapısını tanımlar
 */
export interface Client {
  id: string;
  name: string;
  services: string[];
  logo: string;
  description: string;
  socialLinks: ClientSocialLinks;
  mediaLinks: ClientMediaLinks;
}

/**
 * clientsData
 *
 * - Uygulamada kullanılan tüm müşterilerin listesi.
 * - İhtiyaç halinde bu listeden öğe bulunup ClientWorkDetails bileşeninde gösterilir.
 * - Aşağıdaki güncelleme: Kullanıcının talebi doğrultusunda şu markaların videoları
 *   orijinal medya olarak eklendi: Mahmood, Mondi (Mondi Home), Parex, Pegasus, Pınar, Puffy, Regal.
 *
 * Not: YouTube kısa bağlantıları (https://youtu.be/ID) embed formata (https://www.youtube.com/embed/ID) dönüştürüldü.
 */
export const clientsData: Client[] = [
  { 
    id: 'evy-baby',
    name: 'Evy Baby', 
    services: ['Digital Marketing', 'Media Planning', 'Influencer Marketing', 'Social Media', 'Content Creation', 'Adaptation', 'Sampling'], 
    logo: 'https://www.evybaby.com/ui/img/Evy%20Baby%20Logo.png',
    description: 'Media Planning\nSM Management\nContent Creation\nInfluencer Marketing',
    socialLinks: {
      facebook: 'https://www.facebook.com/EvyBabyIraq',
      instagram: 'https://www.instagram.com/evybabyiraq/',
      youtube: 'https://www.youtube.com/@evybabyiraq'
    },
    mediaLinks: {
      videos: ['https://www.youtube.com/embed/KAvUvOXvS04?rel=0', 'https://www.youtube.com/embed/-BdCPhCiq_M?rel=0', 'https://www.instagram.com/reel/DIRdsmbsMBo/embed/', 'https://www.youtube.com/embed/d4COw--InOs?si=diErWkpeo_6cPOwb', 'https://www.youtube.com/embed/FUlc92KVNI8?rel=0', 'https://www.youtube.com/embed/8_ac9G2t_h0?rel=0', 'https://www.instagram.com/reel/DIjoyY5MOkw/embed/'],
      images: ['https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/6493ccc9-5ab8-46dc-b753-da3b1a0a8071.png', 'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/a169096f-da36-4e73-a4af-31c4e4a5f1ed.png']
    }
  },
  { 
    id: 'eti',
    name: 'Eti', 
    services: ['Digital Marketing', 'Media Planning', 'Influencer Marketing', 'Social Media', 'Adaptation', 'Sampling'], 
    logo: 'https://www.ceylanbranda.com/wp-content/uploads/2020/06/etii-400x200.png',
    description: 'Media Planning, Digital & Influencer Marketing',
    socialLinks: {
      facebook: 'https://www.facebook.com/eti',
      instagram: 'https://www.instagram.com/eti',
      youtube: 'https://www.youtube.com/channel/eti'
    },
    mediaLinks: {
      videos: ['https://www.youtube.com/embed/MCiTcgQctj4?si=Ytl0VNYuh4WaCsfF', 'https://www.youtube.com/embed/TDJ7MXMzpBU?si=fwTP81M00jvxapVf', 'https://www.instagram.com/reel/DExhItvsUx5/embed/', 'https://www.youtube.com/embed/8i6b-vK9Ql0', 'https://www.youtube.com/embed/2p4Ra-LvykQ'],
      images: ['https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/a8f04a9b-eb01-410d-8ac2-94dec1200b9f.jpg', 'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/de1e7857-9104-4399-82e8-2fd703a799d2.jpg']
    }
  },
  { 
    id: 'talabat',
    name: 'Talabat', 
    services: ['Media Planning'], 
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/b3/Talabat_logo.svg/1200px-Talabat_logo.svg.png',
    description: 'Strategic media planning for food delivery platform',
    socialLinks: {
      facebook: 'https://www.facebook.com/talabat',
      instagram: 'https://www.instagram.com/talabat'
    },
    mediaLinks: {
      videos: ['https://www.youtube.com/embed/SjbmVzwCFW8?rel=0', 'https://www.youtube.com/embed/jaBGZTjIgV0?rel=0'],
      images: ['https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/e30e394a-92e1-4cb4-ba08-b9d2650608e3.jpg']
    }
  },
  { 
    id: 'geneva-call',
    name: 'Geneva Call', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://landportal.org/sites/default/files/2024-03/GC-Logo-Official-English-Color.jpg',
    description: 'Media planning and adaptation for humanitarian organization',
    socialLinks: {
      linkedin: 'https://linkedin.com/company/genevacall'
    },
    mediaLinks: {
      videos: ['https://www.youtube.com/embed/uT6PddTSOOk?si=WmN4Q56r-VsBnZFG', 'https://www.youtube.com/embed/5errOa0SbDA?si=5RcYUwKPIuw5Kh87', 'https://www.youtube.com/embed/O_sO_MXxeBo?si=8t9k7bJ-0PI7xJDO'],
      images: ['https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/36b0360f-67c9-44c1-b304-8521aa4e137d.jpg', 'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/d8175dbb-2db2-4656-946f-eca565844b74.jpg']
    }
  },
  { 
    id: 'korek',
    name: 'KOREK', 
    services: ['Corporate Identity'], 
    logo: 'https://www.korektel.com/media/logos/Korek_En.svg',
    description: 'Corporate identity development for telecommunications company',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  },
  { 
    id: 'turkish-airlines',
    name: 'TURKISH AIRLINES', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://www.freelogovectors.net/wp-content/uploads/2023/10/turkish-airlines-logo-freelogovectors.net_.png',
    description: 'Media planning and adaptation for airline services',
    socialLinks: {},
    mediaLinks: { videos: ['https://www.youtube.com/embed/dHt4TGIi8Mo'], images: [] }
  },
  { 
    id: 'upcare-hair2000',
    name: 'UPCARE HAIR2000', 
    services: ['Media Planning', 'Influencer Marketing', 'Social Media', 'Content Creation', 'Adaptation'], 
    logo: 'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/8ff30bed-011d-4b6f-b000-26f7a444e70c.png',
    description: 'Comprehensive marketing for hair care products',
    socialLinks: {},
    mediaLinks: {
      videos: [
        'https://www.youtube.com/embed/fdFiP3JhnhI?rel=0',
        'https://www.youtube.com/embed/SywWtgpZSGA?rel=0',
        'https://www.youtube.com/embed/WYbujs4EhBI?rel=0',
        'https://www.youtube.com/embed/fkoAKQvTEiw?rel=0',
        'https://www.youtube.com/embed/mqoGrH1AkPs?rel=0',
        'https://www.instagram.com/khandool/p/C9u6F5IsWc_/embed//',
        'https://www.instagram.com/sumaya_goran/p/C8r_ZDooB_T/embed'
      ],
      images: []
    }
  },
  { 
    id: 'istikbal',
    name: 'Istikbal', 
    services: ['Digital Marketing', 'Media Planning', 'Influencer Marketing', 'Social Media', 'Content Creation', 'Adaptation', 'Production'], 
    logo: 'https://istikbal.com.tr/class/INNOVAEditor/assets/hakkimizda/istikballogo17.png',
    description: 'Marketing strategy for furniture brand',
    socialLinks: {},
    mediaLinks: {
      videos: [
        'https://www.youtube.com/embed/R3D1Fo5WhWI?rel=0',
        'https://www.youtube.com/embed/e2O5jhql4p0?rel=0',
        'https://www.youtube.com/embed/sP4QvDwqQVA?rel=0',
        'https://www.instagram.com/welyanalbaiaty94/p/DQCPdSGAktu/',
        'https://www.instagram.com/zahraa.sami/p/DQe3KP6iHy3/embed/'
      ],
      images: []
    }
  },
  { 
    id: 'selpak',
    name: 'Selpak', 
    services: ['Digital Marketing', 'Media Planning', 'Influencer Marketing', 'Social Media', 'Adaptation'], 
    logo: 'https://www.selpak.com.tr/getmedia/641c8537-2645-44dc-b1d6-e7138b4af7f6/logo.png?width=499&height=416&ext=.png',
    description: 'Marketing campaigns for paper products',
    socialLinks: {},
    mediaLinks: {
      videos: [
        'https://www.youtube.com/embed/jjU1-g6lX2c', 
        'https://www.youtube.com/embed/pXnW1-gNEZE?si=jTrvJibLBRLQ9Min', 
        'https://www.instagram.com/reel/DHvudyyqM4n/embed/',
        'https://www.instagram.com/reel/DHvwtXdKPLd/embed/'
      ],
      images: []
    }
  },
  { 
    id: 'vestel',
    name: 'Vestel', 
    services: ['Media Planning'], 
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/8b/Vestel_logo.svg/2560px-Vestel_logo.svg.png',
    description: 'Media planning for electronics company',
    socialLinks: {},
    mediaLinks: { 
      videos: ['https://www.youtube.com/embed/SV4iWR6NmX8', 'https://www.youtube.com/embed/GkWlNSXV_BY'], 
      images: [] 
    }
  },
  // Bellona client — add the requested videos into its original media videos array
  { 
    id: 'bellona',
    name: 'Bellona', 
    services: ['Digital Marketing', 'Media Planning', 'Influencer Marketing', 'Social Media', 'Adaptation', 'Production'], 
    logo: 'https://brandlogos.net/wp-content/uploads/2013/01/bellona-logo-vector.png',
    description: 'Comprehensive marketing for fashion brand',
    socialLinks: {},
    mediaLinks: { 
      videos: [
        // explicit embed URLs provided by user (kept as embed src)
        'https://www.youtube.com/embed/mElmkwssyHU?si=t3GM1oME-t0XwAQG',
        'https://www.youtube.com/embed/8kEmRwZ5JWk?si=YTP-ZDcWaTQx9De5',
        'https://www.youtube.com/embed/7AZnUERXL3U?si=kypJqHK52t3wtm3B',
        'https://www.youtube.com/embed/scxHpf0XIvs?si=Gus-6trKYy7grLT2',
        'https://www.youtube.com/embed/xs6CpTY59-Q?si=_IUH1G9RqNTPsGFS',
        'https://www.youtube.com/embed/KuMIEOwLCWI?si=LVhMLO2lSkFa5B8O',
        'https://www.youtube.com/embed/HxalGmoXG0E?si=VspEfAlgbg-5E1HG',
        'https://www.youtube.com/embed/merkqzWRjmw?si=xvhlDqEubhl_3Tdf',
        'https://www.youtube.com/embed/QJ-cIjrTsLM?si=-jDKM4jTUI4-Vuvu',
        'https://www.youtube.com/embed/3F0n2z_HUrs?si=-pcvax5WSMha27wj',
        // converted youtu.be short links that were listed after the iframes
        'https://www.youtube.com/embed/gEXASbYeRpQ',
        'https://www.youtube.com/embed/9ISWIs0u4Hk',
        'https://www.youtube.com/embed/UhiQQ34doM0'
      ],
      images: []
    }
  },
  { 
    id: 'betek',
    name: 'Betek', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRA1as5D4qFwyUFZ54XoHbg5ClnNzgF3XMuPg&s',
    description: 'Media planning for paint products',
    socialLinks: {},
    mediaLinks: { 
      videos: [
        // added videos for Betek (converted to embed)
        'https://www.youtube.com/embed/9drSdL9Rjh8',
        'https://www.youtube.com/embed/8QtNjmLFdJw'
      ],
      images: []
    }
  },
  { 
    id: 'beymen',
    name: 'Beymen', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://images.seeklogo.com/logo-png/43/2/beymen-logo-png_seeklogo-436215.png',
    description: 'Media planning for fashion retailer',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  },
  { 
    id: 'clix',
    name: 'CLIX', 
    services: ['Media Planning', 'Adaptation', 'Production'], 
    logo: 'https://lh4.googleusercontent.com/proxy/LFWKU9qbSTDhlFBckPCHr35S5TFZZMwHoTC_BhgGMF68vh4NLCwL6_n-JN7ymkq0qc55EzarkQETbD6C8gCZq_YcGiwg2G88MMr7IaU',
    description: 'Media planning for technology services',
    socialLinks: {},
    mediaLinks: { 
      videos: [
        // added Clix video (converted)
        'https://www.youtube.com/embed/eXrAbBmUVjM'
      ], 
      images: [] 
    }
  },
  { 
    id: 'confy-baby',
    name: 'Confy Baby', 
    services: ['Media Planning', 'Social Media', 'Adaptation'], 
    logo: 'https://mme-01-store-assets.s3.ap-south-1.amazonaws.com/brands/Confy.jpg',
    description: 'Media planning for baby products',
    socialLinks: {},
    mediaLinks: { 
      videos: [
        // added Confy Baby videos
        'https://www.youtube.com/embed/rs6GDnUmF38',
        'https://www.youtube.com/embed/XXGrldCBhfA'
      ], 
      images: [] 
    }
  },
  { 
    id: 'defacto',
    name: 'DeFacto', 
    services: ['Digital Marketing', 'Media Planning', 'Influencer Marketing', 'Adaptation'], 
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/ed/DeFacto_logo.svg/1200px-DeFacto_logo.svg.png',
    description: 'Digital marketing for fashion retailer',
    socialLinks: {},
    mediaLinks: { 
      // All media removed for DeFacto per request: no videos, no images, no embeds.
      videos: [],
      images: []
    }
  },
  { 
    id: 'duru',
    name: 'Duru', 
    services: ['Media Planning', 'Influencer Marketing', 'Social Media', 'Adaptation'], 
    logo: 'https://duru.com/wp-content/uploads/2021/08/duru-logo.png',
    description: 'Media planning for personal care products',
    socialLinks: {},
    mediaLinks: { 
      videos: [
        // Instagram pair (embed src) — original URLs:
        // https://www.instagram.com/reel/DSFn6p5DdaR/
        // https://www.instagram.com/reel/DR7fmGHjS41/
        'https://www.instagram.com/reel/DSFn6p5DdaR/embed/',
        'https://www.instagram.com/reel/DR7fmGHjS41/embed/',
        // Existing YouTube embeds preserved
        'https://www.youtube.com/embed/quRc_msMW98',
        'https://www.youtube.com/embed/vve3gOcQcRM',
        'https://www.youtube.com/embed/_iS1JBtVcas',
        'https://www.youtube.com/embed/NsRvwh3XNqY',
        'https://www.youtube.com/embed/t4Hg_FH7xFA',
        'https://www.youtube.com/embed/H7-jvNymchE',
        'https://www.youtube.com/embed/9lK3ZHwyINg'
      ], 
      images: [] 
    }
  },
  { 
    id: 'dyo',
    name: 'DYO', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://images.seeklogo.com/logo-png/4/2/dyo-logo-png_seeklogo-44696.png',
    description: 'Media planning for paint manufacturer',
    socialLinks: {},
    mediaLinks: { 
      videos: [
        // added DYO video
        'https://www.youtube.com/embed/ZWqwCBALAXk'
      ],
      images: [] 
    }
  },
  { 
    id: 'elvan',
    name: 'Elvan', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://images.seeklogo.com/logo-png/31/2/elvan-gida-logo-png_seeklogo-317671.png',
    description: 'Media planning for food products',
    socialLinks: {},
    mediaLinks: { 
      videos: [
        // added Elvan video
        'https://www.youtube.com/embed/caryweYkwh0'
      ],
      images: [] 
    }
  },
  { 
    id: 'emaar',
    name: 'EMAAR', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/630c9902-2080-4da6-80aa-62115b77bc27.png',
    description: 'Media planning for real estate developer',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  },
  { 
    id: 'emotion',
    name: 'Emotion', 
    services: ['Influencer Marketing', 'Video Adaptation'], 
    logo: 'https://files.sikayetvar.com/lg/cmp/18/180184.png?1747305149',
    description: 'Influencer marketing services',
    socialLinks: {},
    mediaLinks: { 
      videos: [
        'https://www.instagram.com/p/DNyRUuU2gtg/embed/',
        'https://www.instagram.com/p/DOWVA16DHky/embed/',
        'https://www.youtube.com/embed/pv6r3z_4KWo?si=-aaLQYNobqwgN8LV',
        'https://www.youtube.com/embed/JZkQdd-TlLw?si=6CS5pAKk_ZA7AKxA'
      ], 
      images: [] 
    }
  },
  { 
    id: 'fastlink',
    name: 'Fastlink', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://upload.wikimedia.org/wikipedia/commons/e/e9/Fastlink_4G_LTE_internet_service_provider_in_Iraq.png',
    description: 'Media planning for telecommunications',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  },
  { 
    id: 'fly-erbil',
    name: 'Fly Erbil', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://assets.planespotters.net/files/airlines/5/fly-erbil_0972a2_opk.png',
    description: 'Media planning for airline services',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  },
  { 
    id: 'flyfix',
    name: 'Flyfix', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://seeklogo.com/images/F/flyfix-logo-107057EDB3-seeklogo.com.png',
    description: 'Media planning for repair services',
    socialLinks: {},
    mediaLinks: { videos: ['https://www.youtube.com/embed/iWDYPJK9cho'], images: [] }
  },
  { 
    id: 'handicap-international',
    name: 'Handicap International', 
    services: ['Digital Marketing'], 
    logo: 'https://www.viprize.org/images/charities/57_zA5V6vU.png',
    description: 'Digital marketing for humanitarian organization',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  },
  { 
    id: 'highgenic',
    name: 'HighGenic', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://files.sikayetvar.com/lg/cmp/14/145626.png?1616001162',
    description: 'Media planning for beauty products',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  },
  { 
    id: 'joyful',
    name: 'Joyful', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://m.media-amazon.com/images/S/aplus-media-library-service-media/d41af80e-c569-485b-b5f5-bd5a825c32ca.__CR0,0,300,300_PT0_SX300_V1___.jpeg',
    description: 'Media planning and adaptation services',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  },
  { 
    id: 'kalyon',
    name: 'Kalyon', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT5AXo4X1OtDGATRpMwAlCuwx_b3f-pA0lVPA&s',
    description: 'Media planning for construction company',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  },
  { 
    id: 'genc',
    name: 'Genç', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://kayalarkimya.com.tr/wp-content/uploads/2025/02/genc-boya-logo-tr-png.png',
    description: 'Media planning and adaptation services',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  },
  { 
    id: 'kenton',
    name: 'Kenton', 
    services: ['Media Planning', 'Influencer Marketing', 'Social Media', 'Adaptation'], 
    logo: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTbG8yW-2uAD3HlOC8ogja9YEemqSw-O_cuCQ&s',
    description: 'Comprehensive media marketing services',
    socialLinks: {},
    mediaLinks: { 
      videos: [
        // Instagram embed (single) added as requested:
        // original: https://www.instagram.com/reel/CcOhK9SKEMK/
        'https://www.instagram.com/reel/CcOhK9SKEMK/embed/',
        // Existing YouTube embeds preserved
        'https://www.youtube.com/embed/DQA-oWG6weI',
        'https://www.youtube.com/embed/l8AUa-ogVdg',
        'https://www.youtube.com/embed/xQiWFTBhK84',
        'https://www.youtube.com/embed/ddtv_zvBR4U',
        'https://www.youtube.com/embed/x9ajJzDBp58',
        'https://www.youtube.com/embed/-aVhXe_8E6Y'
      ], 
      images: [] 
    }
  },
  { 
    id: 'lc-waikiki',
    name: 'LC Waikiki', 
    services: ['Digital Marketing', 'Adaptation'], 
    logo: 'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/59da5ce1-95a6-4b09-9f03-0b923454435d.png',
    description: 'Digital marketing for fashion retailer',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  },
  { 
    id: 'lia',
    name: 'Lia', 
    services: ['Media Planning', 'Social Media', 'Adaptation'], 
    logo: 'https://www.zergroup.com/upload/source/Lia-Logo.png?1688730819793',
    description: 'Media planning and social media services',
    socialLinks: {},
    mediaLinks: { 
      videos: [
        'https://www.youtube.com/embed/DRDF9HM2k4M?si=siooSNPIjoI7ZCnr',
        'https://www.youtube.com/embed/GGdiTM5eayY?si=h_CPqmxWTKSNtx0n',
        'https://www.instagram.com/p/CqaZHdyNPCw/embed/',
        'https://www.instagram.com/p/Co7PBHdp0u5/embed/'
      ], 
      images: [] 
    }
  },
  { 
    id: 'zer',
    name: 'Zer', 
    services: ['Media Planning', 'Social Media', 'Adaptation'], 
    logo: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRAuP9zmYf0pSmDRoLp5UQTA8-U0hM89F3-lQ&s',
    description: 'Strategic media planning and social media services',
    socialLinks: {
      facebook: 'https://www.facebook.com/zergroup',
      instagram: 'https://www.instagram.com/zergroup'
    },
    mediaLinks: { 
      videos: [
        'https://www.youtube.com/embed/8g-8ELJM__4?si=lFkzm-lvE0MUaRQV',
        'https://www.youtube.com/embed/4qGJolewPOs?si=5e3ObmJw2PdVE4Dg'
      ], 
      images: [] 
    }
  },
  { 
    id: 'magenta',
    name: 'Magenta', 
    services: ['Digital Marketing', 'Media Planning', 'Influencer Marketing', 'Content Creation'], 
    logo: 'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/34262349-0ae1-4670-bd04-f4817c17b76c.png',
    description: 'Digital marketing for technology brand',
    socialLinks: {},
    mediaLinks: { 
      videos: [
        'https://www.instagram.com/reel/C2QEH5pshPS/embed/',
        'https://www.instagram.com/reel/C2SUxaSq9g1/embed/',
        'https://www.youtube.com/embed/6EzfyZ0k1GQ?si=InUS_LNCS82uLhYJ',
        'https://www.youtube.com/embed/3PWmQXWDTOY?si=TEGlXaYjvVCHOEXO',
        'https://www.youtube.com/embed/iZ0RCY18Ds0?si=HD2IyG3m1Qr9JlTR',
        'https://www.youtube.com/embed/WDShAmiTdAw?si=-0Zpp9Q1I4QDRatI'
      ], 
      images: [] 
    }
  },
  { 
    id: 'mahbuba',
    name: 'Mahbuba', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcROkXnXeJmN3UNgMdv9OUJ5POlCownSGBTKZw&s',
    description: 'Media planning and adaptation services',
    socialLinks: {},
    mediaLinks: { 
      videos: [
        'https://www.youtube.com/embed/7Cf_BPQeRKI',
        'https://www.youtube.com/embed/o4HDXE7ZBDY'
      ], 
      images: [] 
    }
  },
  { 
    id: 'mahmood',
    name: 'Mahmood', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://www.middleeastmarket.fr/cdn/shop/collections/MAHMOOD_TEA.jpg?v=1578846171',
    description: 'Media planning and adaptation services',
    socialLinks: {},
    mediaLinks: { 
      // Kullanıcının talebi doğrultusunda eklenen videolar (orijinal video)
      videos: [
        'https://www.youtube.com/embed/4c-D1ACAvO8',
        'https://www.youtube.com/embed/JMqsp6-Twns'
      ], 
      images: [] 
    }
  },
  { 
    id: 'mersin',
    name: 'Mersin', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/a345bb43-3cfa-46cf-af6f-cab1b22a9544.png',
    description: 'Media planning for city promotion',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  },
  { 
    id: 'mondi',
    name: 'Mondi', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/916d7b88-375a-4010-bc29-1cb7bb030ad5.png',
    description: 'Media planning for paper products',
    socialLinks: {},
    mediaLinks: { 
      // "Mondi Home" için eklenen video
      videos: ['https://www.youtube.com/embed/U2iexyl8IWc'], 
      images: [] 
    }
  },
  { 
    id: 'newroz-telecom',
    name: 'Newroz Telecom', 
    services: ['Media Planning'], 
    logo: 'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/45bf874c-e54f-4430-830d-49517d388660.png',
    description: 'Media planning for telecommunications',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  },
  { 
    id: 'onlem',
    name: 'Önlem', 
    services: ['Media Planning', 'Adaptation', 'Influencer Marketing'], 
    logo: 'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/df87d6b6-226f-40f0-82c7-dfbc91d9e0eb.png',
    description: 'Media planning for security services',
    socialLinks: {},
    mediaLinks: { 
      videos: ['https://www.youtube.com/embed/2-3cwmoU7CA', 'https://www.youtube.com/embed/2D2vGh2xrTI', 'https://www.instagram.com/reel/DDckst3MTW9/embed/', 'https://www.instagram.com/reel/DNWCVKvMdG0/embed/'], 
      images: [] 
    }
  },
  { 
    id: 'ona',
    name: 'ONA', 
    services: ['Social Media', 'Content Creation'], 
    logo: 'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/139ca53c-ed9b-47ae-a293-cc4005d47ee5.png',
    description: 'Social media and content creation services',
    socialLinks: {},
    mediaLinks: { 
      videos: ['https://www.youtube.com/embed/6jeeqTKfits'], 
      images: [] 
    }
  },
  { 
    id: 'oppo',
    name: 'Oppo', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://images.seeklogo.com/logo-png/25/1/oppo-logo-png_seeklogo-253623.png',
    description: 'Media planning for electronics brand',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  },
  { 
    id: 'parex',
    name: 'Parex', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://www.parex.com.tr/wp-content/uploads/2025/07/logo.png',
    description: 'Media planning for construction materials',
    socialLinks: {},
    mediaLinks: { 
      // Kullanıcının verdiği YouTube linkleri embed formatında eklendi
      videos: [
        'https://www.youtube.com/embed/vqf9vB6IW7U',
        'https://www.youtube.com/embed/0mii7Kltu-A'
      ],
      images: [] 
    }
  },
  { 
    id: 'pegasus',
    name: 'Pegasus', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/c8c102ad-cf4d-4f47-bdad-807b2311a45b.png',
    description: 'Media planning for airline services',
    socialLinks: {},
    mediaLinks: { 
      // Facebook iframe kaldırıldı; yalnızca YouTube embed kullanılıyor
      videos: ['https://www.youtube.com/embed/zee9Jr37ulo'], 
      images: [] 
    }
  },
  { 
    id: 'peros',
    name: 'Peros', 
    services: ['Media Planning', 'Adaptation', 'Production'], 
    logo: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTp8Aj6y1FixEyYmZASJ96oNncBj_17CvZKlA&s',
    description: 'Media planning and production services',
    socialLinks: {},
    mediaLinks: { 
      videos: ['https://www.youtube.com/embed/23NBVzU2Fx0'],
      images: [] 
    }
  },
  { 
    id: 'pinar',
    name: 'Pınar', 
    services: ['Media Planning', 'Social Media', 'Adaptation'], 
    logo: 'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/2f22bd97-6518-40e5-96e9-546bbe14bbbd.png',
    description: 'Media planning for food products',
    socialLinks: {},
    mediaLinks: { 
      // Kullanıcının sağladığı iki YouTube video embed formatında eklendi
      videos: [
        'https://www.youtube.com/embed/P9YMTarKt0s',
        'https://www.youtube.com/embed/DJJrGOgOeFM'
      ],
      images: [] 
    }
  },
  { 
    id: 'puffy',
    name: 'Puffy', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://puffydeterjan.com.tr/images/logo.png',
    description: 'Media planning and adaptation services',
    socialLinks: {},
    mediaLinks: { 
      videos: [
        // Kullanıcının talep ettiği video eklendi
        'https://www.youtube.com/embed/gqsQK3wWMl8'
      ], 
      images: [] 
    }
  },
  { 
    id: 'regal',
    name: 'Regal', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://logoeps.com/wp-content/uploads/2013/05/regal-vector-logo.png',
    description: 'Media planning and adaptation services',
    socialLinks: {},
    mediaLinks: { 
      // Kullanıcı tarafından gönderilen Regal videosu eklendi
      videos: ['https://www.youtube.com/embed/KMJyNGq0yGQ'],
      images: [] 
    }
  },
  { 
    id: 'sadaf',
    name: 'Sadaf', 
    services: ['Media Planning'], 
    logo: 'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/20035f17-ab6c-46e8-be20-20551a5ecf3c.png',
    description: 'Media planning services',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  },
  { 
    id: 'safe-home',
    name: 'Safe Home', 
    services: ['Media Planning'], 
    logo: 'https://pbs.twimg.com/profile_images/1545110641144479746/ugtKkWln_400x400.jpg',
    description: 'Media planning for home security',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  },
  { 
    id: 'saray',
    name: 'Saray', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://www.saraybiskuvi.com.tr/panel/content/files/Logolar/saray_biskuvi.png',
    description: 'Media planning for food products',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  },
  { 
    id: 'sony',
    name: 'Sony', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/21171ae4-0686-414e-9e5b-c370a1cc471c.jpg',
    description: 'Media planning for electronics brand',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  },
  { 
    id: 'sunich',
    name: 'Sunich', 
    services: ['Social Media'], 
    logo: 'https://d1yjjnpx0p53s8.cloudfront.net/styles/logo-thumbnail/s3/072018/untitled-1_294.png?ZqL2xUu.c2zUL7CtPufFG2hIzXRLKfsK&itok=MUXsgowV',
    description: 'Social media management services',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  },
  { 
    id: 'tat',
    name: 'Tat', 
    services: ['Media Planning', 'Social Media', 'Adaptation'], 
    logo: 'https://mucadelegazete.com/wp-content/uploads/2023/07/TAT-GIDAYA-ALICI-CIKTI-3.png',
    description: 'Media planning for food brand',
    socialLinks: {},
    mediaLinks: { 
      videos: [
        'https://www.youtube.com/embed/SPluD8Mo7b8?si=vK9wOEa51y2ZSTNk',
        'https://www.instagram.com/reel/CfWdZYFACeM/embed/',
        'https://www.instagram.com/p/Cy-6Fu_iz0o/embed/'
      ], 
      images: [] 
    }
  },
  { 
    id: 'uludag',
    name: 'Uludağ', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/5d664db5-aaed-46a1-9c4b-aa81fd634ada.png',
    description: 'Media planning for beverage company',
    socialLinks: {},
    mediaLinks: { 
      videos: ['https://www.youtube.com/embed/7JajqRC94cU?si=Et5S_RY2sbMdgOzE', 'https://www.youtube.com/embed/MwnLRw0NZFs'], 
      images: [] 
    }
  },
  { 
    id: 'xiaomi',
    name: 'Xiaomi', 
    services: ['Media Planning', 'Adaptation'], 
    logo: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS0sjdNUizS-5JgBpa7Q3J16ZoSIxo5SdtpzT4rufsB7DybMU4gDSAxg7hVNxK0cGrirEo&usqp=CAU',
    description: 'Media planning for technology brand',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  },
  // Additional placeholder clients to reach 81 total
  { 
    id: 'client-74',
    name: 'Client 74', 
    services: [], 
    logo: 'https://edicoo.com/public/uploads/logo.png',
    description: 'Marketing services provided by Edicoo Agency',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  },
  { 
    id: 'client-75',
    name: 'Client 75', 
    services: [], 
    logo: 'https://edicoo.com/public/uploads/logo.png',
    description: 'Marketing services provided by Edicoo Agency',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  },
  { 
    id: 'client-76',
    name: 'Client 76', 
    services: [], 
    logo: 'https://edicoo.com/public/uploads/logo.png',
    description: 'Marketing services provided by Edicoo Agency',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  },
  { 
    id: 'client-77',
    name: 'Client 77', 
    services: [], 
    logo: 'https://edicoo.com/public/uploads/logo.png',
    description: 'Marketing services provided by Edicoo Agency',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  },
  { 
    id: 'client-78',
    name: 'Client 78', 
    services: [], 
    logo: 'https://edicoo.com/public/uploads/logo.png',
    description: 'Marketing services provided by Edicoo Agency',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  },
  { 
    id: 'client-79',
    name: 'Client 79', 
    services: [], 
    logo: 'https://edicoo.com/public/uploads/logo.png',
    description: 'Marketing services provided by Edicoo Agency',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  },
  { 
    id: 'client-80',
    name: 'Client 80', 
    services: [], 
    logo: 'https://edicoo.com/public/uploads/logo.png',
    description: 'Marketing services provided by Edicoo Agency',
    socialLinks: {},
    mediaLinks: { videos: [], images: [] }
  }
  // Total: 81 clients (10 original + 71 new)
];